import type { FC, ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router';

import type { TabConfig } from '@zen/DesignSystem';
import { Tabs } from '@zen/DesignSystem';

export interface RouteTabConfig {
  className?: string;
  component: ReactNode;
  isDefault?: boolean;
  label: ReactNode;
  path: string;
  totalCount?: number;
  url: string;
}

interface Props {
  preserveSearchParams?: boolean;
  tabsConfig: RouteTabConfig[];
}

const RouteTabs: FC<Props> = ({ preserveSearchParams = true, tabsConfig }) => {
  const navigate = useNavigate();
  const { pathname, search } = useLocation();

  const prepareConfigForTabs = (config: RouteTabConfig[]): TabConfig[] => {
    return config.map((routeTabConfig: RouteTabConfig): TabConfig => {
      const { label, totalCount, className, url } = routeTabConfig;

      return {
        isActive: pathname.includes(url),
        label,
        value: preserveSearchParams ? `${url}${search}` : url,
        totalCount,
        className
      };
    });
  };

  const handleTabClick = (tab: string): void | Promise<void> => {
    // Split path and search params if present
    const [path, searchParams] = tab.split('?');
    const searchString = searchParams ? `?${searchParams}` : '';

    // If the tab URL is absolute (starts with /), navigate to the absolute path
    if (path.startsWith('/')) {
      navigate({ pathname: path, search: searchString });
    } else {
      // For relative paths, use relative navigation to go up one level, then into the target path
      // This replaces the current route segment instead of appending to it
      // Example: from /parent/current to /parent/target (not /parent/current/target)
      navigate({ pathname: `../${path}`, search: searchString }, { relative: 'path' });
    }
  };

  return <Tabs onTabClick={handleTabClick} tabsConfig={prepareConfigForTabs(tabsConfig)} />;
};

export default RouteTabs;
